// shoppingListService.js - Consolidated Family Grocery & Shopping List Utility
import { parseFraction, formatFraction, toTitleCase } from "./recipeStandardization";

const STORAGE_KEY = "astrup_shopping_list";

export const getShoppingList = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.warn("Could not read shopping list:", e);
    return [];
  }
};

export const saveShoppingList = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event("shopping_list_updated"));
  } catch (e) {
    console.warn("Could not save shopping list:", e);
  }
};

export const addIngredientsToShoppingList = (ingredients = [], recipeTitle = "") => {
  const currentList = getShoppingList();
  const updatedList = [...currentList];

  ingredients.forEach(ing => {
    const rawName = ing.ingredient_name || ing.name || "";
    if (!rawName.trim()) return;

    const cleanName = toTitleCase(rawName);
    const cleanUnit = (ing.measurement_name || ing.measurement_unit || "").trim().toLowerCase();
    const cleanAmount = ing.amount ? String(ing.amount).trim() : "";

    // Check if item with same name and same unit already exists
    const existingIndex = updatedList.findIndex(
      item => item.name.toLowerCase() === cleanName.toLowerCase() &&
              (item.unit || "").toLowerCase() === cleanUnit &&
              !item.completed
    );

    if (existingIndex >= 0) {
      // Sum amounts if both are numeric
      const existing = updatedList[existingIndex];
      const parsedExist = parseFraction(existing.amount);
      const parsedNew = parseFraction(cleanAmount);

      if (typeof parsedExist === "number" && typeof parsedNew === "number") {
        existing.amount = formatFraction(parsedExist + parsedNew);
      }
      if (recipeTitle && !existing.sources.includes(recipeTitle)) {
        existing.sources.push(recipeTitle);
      }
    } else {
      updatedList.push({
        id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        name: cleanName,
        amount: cleanAmount,
        unit: cleanUnit,
        sources: recipeTitle ? [recipeTitle] : [],
        completed: false,
        addedAt: Date.now()
      });
    }
  });

  saveShoppingList(updatedList);
  return updatedList;
};

export const addCustomShoppingItem = (name, amount = "", unit = "") => {
  if (!name || !name.trim()) return;
  const currentList = getShoppingList();
  currentList.push({
    id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    name: toTitleCase(name),
    amount: amount.trim(),
    unit: unit.trim(),
    sources: [],
    completed: false,
    addedAt: Date.now()
  });
  saveShoppingList(currentList);
  return currentList;
};

export const toggleShoppingItem = (id) => {
  const currentList = getShoppingList();
  const updated = currentList.map(item => {
    if (item.id === id) {
      return { ...item, completed: !item.completed };
    }
    return item;
  });
  saveShoppingList(updated);
  return updated;
};

export const deleteShoppingItem = (id) => {
  const currentList = getShoppingList();
  const updated = currentList.filter(item => item.id !== id);
  saveShoppingList(updated);
  return updated;
};

export const clearCompletedShoppingItems = () => {
  const currentList = getShoppingList();
  const updated = currentList.filter(item => !item.completed);
  saveShoppingList(updated);
  return updated;
};

export const clearAllShoppingItems = () => {
  saveShoppingList([]);
  return [];
};

export const formatShoppingListAsText = () => {
  const list = getShoppingList();
  if (list.length === 0) return "Astrup Family Grocery List is empty.";

  const pending = list.filter(i => !i.completed);
  const done = list.filter(i => i.completed);

  let text = "🛒 Astrup Family Grocery List\n";
  text += "=============================\n\n";

  if (pending.length > 0) {
    text += "TO BUY:\n";
    pending.forEach(item => {
      const amt = item.amount ? `${item.amount} ` : "";
      const unt = item.unit ? `${item.unit} ` : "";
      const src = item.sources?.length > 0 ? ` (for ${item.sources.join(", ")})` : "";
      text += `• ${amt}${unt}${item.name}${src}\n`;
    });
    text += "\n";
  }

  if (done.length > 0) {
    text += "ALREADY PICKED UP:\n";
    done.forEach(item => {
      text += `✓ ${item.name}\n`;
    });
  }

  return text.trim();
};
